import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Row, Select } from 'antd';
import { FormOutlined, SettingOutlined } from '@ant-design/icons';

import BorderlessButton from '../../common/buttons/BorderlessButton';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';

import {
  updateData,
} from './workflowHelpers';
import { PRIORITY_OPTS, STATUS_OPTS } from '../../helpers/subtasks.constants';
import useToggle from '../../common/hooks/useToggle';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';
import { mergeSets } from '../../helpers/helpers';
import config from '../../config';

const requiredFields = new Set(['title']);
const HOVER_TEXT = 'Use this step to create a new task';
const DISABLED_TEXT = 'Add at least one Text Field to enable this step';
const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the following fields to be configured: <br />
      - <b>Select Priority</b><br />
      - <b>Select Status</b><br />
      - <b>Task Title Field Mapping</b> (set via <b>Configure</b>)
    </div>
  ),
};

const subtaskFields = [
  {
    key: 'title',
    text: 'Task Title',
    type: 'text',
    dataType: undefined,
  },
  {
    key: 'description',
    text: 'Task Description',
    type: 'text',
    dataType: undefined,
  },
  {
    key: 'assigneeId',
    text: 'Assigned User',
    type: 'dropdown',
    dataType: 'Users',
  },
  {
    key: 'dueDate',
    text: 'Due Date',
    type: 'dateTime',
    dataType: undefined,
  },
  {
    key: 'attachments',
    text: 'Attachments',
    type: 'attachment',
    dataType: undefined,
  },
];

const validDataTypes = new Set(['Equipment', 'Cards']);

export default function WorkflowCreateTaskNode({
  setDataMap,
  setElements,
  isDisplay,
  sections = [],
  setDisableNext,
  divisionIds = [],
} = {}) {
  return function innerFunc({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const {
      fieldMappings: initialFieldMappings = {},
      priority: initialPriority,
      status: initialStatus,
      linkFieldId: initialLinkFieldId,
      staticAssigneeId: initialStaticAssigneeId,
    } = data;

    const { toggle, isToggled } = useToggle();

    const [selectedPriority, setSelectedPriority] = useState(initialPriority);
    const [selectedStatus, setSelectedStatus] = useState(initialStatus);
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [linkFieldId, setLinkFieldId] = useState(initialLinkFieldId);
    const [staticAssigneeId, setStaticAssigneeId] = useState(initialStaticAssigneeId);

    const allDivisions = useSelector((state) => state.settings.divisions);
    const users = useSelector((state) => state.users.users);

    useEffect(() => {
      if (!setDisableNext) return;

      const disableNext = !selectedPriority || !selectedStatus || !fieldMappings?.title;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [selectedPriority, selectedStatus, fieldMappings, setDisableNext, id]);

    const onFieldMappingsChange = useCallback((newMappings) => {
      if (!setDataMap || !id) return;
      setFieldMappings(newMappings);
      setDataMap(updateData(id, { fieldMappings: newMappings }));
    }, [setDataMap, id]);

    const onPriorityChange = useCallback((newPriority) => {
      if (!setDataMap || !id) return;
      setSelectedPriority(newPriority);
      setDataMap(updateData(id, { priority: newPriority }));
    }, [setDataMap, id]);

    const onStatusChange = useCallback((newStatus) => {
      if (!setDataMap || !id) return;
      setSelectedStatus(newStatus);
      setDataMap(updateData(id, { status: newStatus }));
    }, [setDataMap, id]);

    const dropdownFields = useMemo(() => (
      sections
        .flatMap(
          ({ fields }) => (
            fields?.filter(
              ({ selectedType, configProps: { dataType } }) => (
                selectedType === 'dropdown' && validDataTypes.has(dataType)
              ),
            ) ?? []
          ),
        )
        .map(
          ({ id: fieldId, configProps }) => ({
            value: fieldId,
            label: configProps?.title,
            type: configProps?.dataType,
          }),
        )
    ), [sections]);

    const mappingFields = useMemo(() => (
      staticAssigneeId
        ? subtaskFields.filter(({ key }) => key !== 'assigneeId')
        : subtaskFields
    ), [staticAssigneeId]);

    const userOptions = useMemo(() => {
      const activeUsers = users.filter((user) => user.active);
      let filteredUsers = activeUsers;
      if (divisionIds.length) {
        const selectedDivisions = new Set(divisionIds);
        const relevantDivisionUsers = mergeSets(
          Object.values(allDivisions)
            .filter(({ id: divisionId }) => selectedDivisions.has(divisionId))
            .map(({ users: divisionUsers }) => divisionUsers),
        );
        filteredUsers = activeUsers.filter((user) => relevantDivisionUsers.has(user.id));
      }
      return filteredUsers.map((user) => ({
        value: user.id,
        label: user.name,
      }));
    }, [users, allDivisions, divisionIds]);

    const onLinkFieldChange = useCallback((newLinkField) => {
      if (!setDataMap || !id) return;

      const relevantField = dropdownFields.find(({ value }) => value === newLinkField);

      setLinkFieldId(newLinkField);
      setDataMap(updateData(id, { linkFieldId: newLinkField, linkFieldType: relevantField?.type }));
    }, [setDataMap, id, dropdownFields]);

    const onStaticAssigneeChange = useCallback((newAssigneeId) => {
      if (!setDataMap || !id) return;
      setStaticAssigneeId(newAssigneeId);
      const updates = { staticAssigneeId: newAssigneeId };
      if (newAssigneeId && fieldMappings.assigneeId) {
        const newMappings = { ...fieldMappings };
        delete newMappings.assigneeId;
        setFieldMappings(newMappings);
        updates.fieldMappings = newMappings;
      }
      setDataMap(updateData(id, updates));
    }, [setDataMap, id, fieldMappings]);

    return (
      <WorkflowActionNode
        title="Create Task"
        Icon={FormOutlined}
        type="createSubtask"
        id={id}
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={
          !selectedPriority || !selectedStatus || !fieldMappings?.title
            ? ERROR_DETAILS
            : undefined
          }
      >
        {!draggable && (
          <div>
            <Row style={{ marginTop: 10 }}>
              Select Priority
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onPriorityChange}
                value={selectedPriority}
                showSearch
                options={PRIORITY_OPTS}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select Status
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onStatusChange}
                value={selectedStatus}
                showSearch
                options={STATUS_OPTS}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select Link Field
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onLinkFieldChange}
                value={linkFieldId}
                showSearch
                options={dropdownFields}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select Assigned User
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onStaticAssigneeChange}
                value={staticAssigneeId}
                showSearch
                allowClear
                optionFilterProp="label"
                options={userOptions}
                disabled={!!fieldMappings.assigneeId}
              />
            </Row>
            <BorderlessButton
              title="Configure"
              style={{ margin: 5 }}
              iconNode={<SettingOutlined />}
              onClick={toggle}
            />
            <WorkflowSimpleMappingConfigureDrawer
              id={id}
              visible={isToggled}
              onClose={toggle}
              onSubmit={toggle}
              sourceSections={sections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
              data={data}
              setDataMap={setDataMap}
              fields={mappingFields}
              requiredFields={requiredFields}
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
}
